/**
 * @file analyticsAggregations.ts
 * @description Dedicated aggregation mathematics and algorithmic synthesis for TableGreet Analytics.
 * 
 * CORE RESPONSIBILITIES:
 * 1. Computes end-to-end 5-step conversion funnel with drop-off percentages.
 * 2. Compiles 4-vector item performance matrix (Views, Orders, Revenue, Conversion %).
 * 3. Aggregates category distribution share.
 * 4. Extracts search demand & zero-result demand gaps.
 * 5. Correlates traffic attribution (QR vs Instagram vs Google vs Direct) with revenue and orders.
 * 6. Evaluates peak operational periods (browsing vs ordering vs revenue).
 * 7. Synthesizes automated executive insight findings.
 */

import type {
  ConversionFunnelMetrics,
  ItemConversionMetric,
  ItemPerformanceMetric,
  CategoryPopularityMetric,
  SearchInsightItem,
  ChannelMetric,
  SourceAttributionMetric,
  PeakPeriodsMetric,
  AutomatedInsightFinding,
} from '../types';

/**
 * Calculates the multi-step ordering funnel with step-to-step drop-offs.
 */
export function computeFunnelMetrics(
  sessionsSet: Set<string>,
  rawEvents: any[],
  orders: any[],
  totalPageViews: number
): ConversionFunnelMetrics {
  let itemViews = 0;
  let ordersStarted = 0;
  let ordersSubmitted = 0;

  rawEvents.forEach((evt) => {
    const meta = (evt.metadata && typeof evt.metadata === 'object' ? evt.metadata : {}) as Record<string, any>;
    const eventName = meta.event_name || evt.event_type;

    if (eventName === 'item_view' || evt.event_type === 'item_interaction' || evt.event_type === 'item_view') {
      itemViews++;
    } else if (eventName === 'order_started' || eventName === 'order_item_added') {
      ordersStarted++;
    } else if (eventName === 'order_submitted') {
      ordersSubmitted++;
    }
  });

  const totalSessions = sessionsSet.size > 0 ? sessionsSet.size : totalPageViews;
  const isCompleted = (status: unknown) => String(status || '').toUpperCase() === 'COMPLETED';
  const completedOrders = orders.filter((o) => isCompleted(o.status)).length;
  const submittedOrders = ordersSubmitted > 0 ? ordersSubmitted : orders.length;

  const sessionToOrderRate = totalSessions > 0 ? Number(((completedOrders / totalSessions) * 100).toFixed(1)) : 0;
  const itemToOrderRate = itemViews > 0 ? Number(((completedOrders / itemViews) * 100).toFixed(1)) : 0;
  const startedToSubmittedRate = ordersStarted > 0 ? Number(((submittedOrders / ordersStarted) * 100).toFixed(1)) : 0;
  const submittedToCompletedRate = submittedOrders > 0 ? Number(((completedOrders / submittedOrders) * 100).toFixed(1)) : 0;

  return {
    sessions: totalSessions,
    item_views: itemViews,
    orders_started: ordersStarted,
    orders_submitted: submittedOrders,
    orders_completed: completedOrders,
    session_to_order_rate: sessionToOrderRate,
    item_to_order_rate: itemToOrderRate,
    order_started_to_submitted_rate: startedToSubmittedRate,
    submitted_to_completed_rate: submittedToCompletedRate,
    overall_conversion_rate: sessionToOrderRate,
    // Backward compatibility aliases
    cart_adds: ordersStarted,
    orders_placed: completedOrders > 0 ? completedOrders : submittedOrders,
    conversion_rate: sessionToOrderRate,
  };
}

/**
 * Calculates item conversions across Views, Orders, Revenue, and Conversion Rate.
 */
export function computeItemConversions(
  items: any[],
  orderItems: any[],
  rawEvents: any[]
): {
  conversions: ItemConversionMetric[];
  topInteracted: ItemPerformanceMetric[];
  topOrdered: ItemPerformanceMetric[];
  topRevenue: ItemPerformanceMetric[];
  topConversion: ItemPerformanceMetric[];
} {
  const itemMap = new Map<string, { id: string; name: string; views: number; orders: number; revenue: number }>();

  // Initialize with catalog items
  items.forEach((item) => {
    itemMap.set(item.id, {
      id: item.id,
      name: item.name || 'Dish',
      views: 0,
      orders: 0,
      revenue: 0,
    });
  });

  // Track views from telemetry
  rawEvents.forEach((evt) => {
    const meta = (evt.metadata && typeof evt.metadata === 'object' ? evt.metadata : {}) as Record<string, any>;
    const eventName = meta.event_name || evt.event_type;

    if (eventName === 'item_view' || evt.event_type === 'item_interaction' || evt.event_type === 'item_view') {
      const itemId = meta.item_id;
      const itemName = meta.item_name || 'Dish';

      if (itemId && itemMap.has(itemId)) {
        itemMap.get(itemId)!.views += 1;
      } else {
        // Match or create by name if ID was absent
        let found = Array.from(itemMap.values()).find((i) => i.name.toLowerCase() === itemName.toLowerCase());
        if (!found) {
          found = { id: itemId || itemName, name: itemName, views: 0, orders: 0, revenue: 0 };
          itemMap.set(found.id, found);
        }
        found.views += 1;
      }
    }
  });

  // Track orders and revenue from authoritative order items
  orderItems.forEach((oi) => {
    const itemId = oi.item_id;
    const qty = Number(oi.quantity) || 1;
    const price = Number(oi.price) || 0;
    const rev = qty * price;

    if (itemId && itemMap.has(itemId)) {
      const entry = itemMap.get(itemId)!;
      entry.orders += qty;
      entry.revenue += rev;
    }
  });

  const conversions: ItemConversionMetric[] = Array.from(itemMap.values()).map((i) => ({
    id: i.id,
    name: i.name,
    views: i.views,
    orders: i.orders,
    revenue: i.revenue,
    conversion_rate: i.views > 0 ? Number(((i.orders / i.views) * 100).toFixed(1)) : 0,
  }));

  const topInteracted = [...conversions]
    .sort((a, b) => b.views - a.views)
    .slice(0, 5)
    .map((c) => ({ name: c.name, count: c.views }));

  const topOrdered = [...conversions]
    .sort((a, b) => b.orders - a.orders)
    .slice(0, 5)
    .map((c) => ({ name: c.name, count: c.orders }));

  const topRevenue = [...conversions]
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5)
    .map((c) => ({ name: c.name, count: c.revenue }));

  const topConversion = [...conversions]
    .filter((c) => c.views >= 3) // Minimum view threshold to prevent skew
    .sort((a, b) => b.conversion_rate - a.conversion_rate)
    .slice(0, 5)
    .map((c) => ({ name: c.name, count: c.conversion_rate }));

  return { conversions, topInteracted, topOrdered, topRevenue, topConversion };
}

/**
 * Computes category distribution share across menu interactions.
 */
export function computeCategoryPopularity(
  categories: any[],
  items: any[],
  orderItems: any[],
  rawEvents: any[]
): CategoryPopularityMetric[] {
  const categoryCountMap = new Map<string, number>();
  const itemToCategoryMap = new Map<string, string>();

  // Map items to category names
  items.forEach((item) => {
    const cat = categories.find((c) => c.id === item.category_id);
    const catName = cat?.name || 'Other';
    itemToCategoryMap.set(item.id, catName);
  });

  // Track category counts from orders and interactions
  orderItems.forEach((oi) => {
    const catName = itemToCategoryMap.get(oi.item_id) || 'Special';
    categoryCountMap.set(catName, (categoryCountMap.get(catName) || 0) + (Number(oi.quantity) || 1));
  });

  rawEvents.forEach((evt) => {
    const meta = (evt.metadata && typeof evt.metadata === 'object' ? evt.metadata : {}) as Record<string, any>;
    if (meta.category_name) {
      categoryCountMap.set(meta.category_name, (categoryCountMap.get(meta.category_name) || 0) + 1);
    }
  });

  const total = Array.from(categoryCountMap.values()).reduce((sum, c) => sum + c, 0) || 1;

  return Array.from(categoryCountMap.entries())
    .map(([category_name, count]) => ({
      category_name,
      count,
      percentage: Math.round((count / total) * 100),
    }))
    .sort((a, b) => b.percentage - a.percentage);
}

/**
 * Extracts popular search queries and unmet search queries (zero results).
 */
export function computeSearchDemand(rawEvents: any[]): {
  topSearches: SearchInsightItem[];
  unmetSearches: SearchInsightItem[];
} {
  const searchMap = new Map<string, number>();
  const unmetMap = new Map<string, number>();

  rawEvents.forEach((evt) => {
    const meta = (evt.metadata && typeof evt.metadata === 'object' ? evt.metadata : {}) as Record<string, any>;
    const eventName = meta.event_name || evt.event_type;

    if (eventName === 'item_search' || evt.event_type === 'search') {
      const q = String(meta.query || '').trim().toLowerCase();
      if (q) searchMap.set(q, (searchMap.get(q) || 0) + 1);
    } else if (eventName === 'search_no_results' || evt.event_type === 'search_no_results') {
      const q = String(meta.query || '').trim().toLowerCase();
      if (q) unmetMap.set(q, (unmetMap.get(q) || 0) + 1);
    }
  });

  const topSearches = Array.from(searchMap.entries())
    .map(([query, count]) => ({ query, count, has_results: true }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const unmetSearches = Array.from(unmetMap.entries())
    .map(([query, count]) => ({ query, count, has_results: false }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  return { topSearches, unmetSearches };
}

/**
 * Correlates traffic acquisition sources with orders and revenue.
 */
export function computeTrafficAttribution(
  rawEvents: any[],
  orders: any[]
): {
  trafficSources: ChannelMetric[];
  sourceAttributions: SourceAttributionMetric[];
  deviceBreakdown: ChannelMetric[];
} {
  const sourceSessionsMap = new Map<string, Set<string>>();
  const deviceSessionsMap = new Map<string, Set<string>>();

  rawEvents.forEach((evt) => {
    const meta = (evt.metadata && typeof evt.metadata === 'object' ? evt.metadata : {}) as Record<string, any>;
    const sessionId = String(meta.session_id || meta.table_id || evt.id || 'session');

    if (meta.source) {
      const src = String(meta.source).toLowerCase();
      if (!sourceSessionsMap.has(src)) {
        sourceSessionsMap.set(src, new Set());
      }
      sourceSessionsMap.get(src)!.add(sessionId);
    }

    if (meta.device_type) {
      const dev = String(meta.device_type).toLowerCase();
      if (!deviceSessionsMap.has(dev)) {
        deviceSessionsMap.set(dev, new Set());
      }
      deviceSessionsMap.get(dev)!.add(sessionId);
    }
  });

  const sourceStats = new Map<string, { sessions: number; orders: number; revenue: number }>();
  sourceSessionsMap.forEach((sessionsSet, src) => {
    sourceStats.set(src, {
      sessions: sessionsSet.size,
      orders: 0,
      revenue: 0,
    });
  });

  // Attribute completed orders to traffic sources where session context matches
  orders.forEach((o) => {
    const meta = (o.metadata && typeof o.metadata === 'object' ? o.metadata : {}) as Record<string, any>;
    const src = String(meta.source || 'qr').toLowerCase();
    if (!sourceStats.has(src)) {
      sourceStats.set(src, { sessions: 1, orders: 0, revenue: 0 });
    }
    const stat = sourceStats.get(src)!;
    stat.orders += 1;
    if (String(o.status || '').toUpperCase() === 'COMPLETED') {
      stat.revenue += Number(o.total_amount) || 0;
    }
  });

  const totalSessions = Array.from(sourceStats.values()).reduce((sum, s) => sum + s.sessions, 0) || 1;

  const sourceAttributions: SourceAttributionMetric[] = Array.from(sourceStats.entries()).map(([source, stats]) => ({
    source: source === 'qr' ? 'QR Codes' : source.charAt(0).toUpperCase() + source.slice(1),
    sessions: stats.sessions,
    orders: stats.orders,
    revenue: stats.revenue,
    share_percentage: Math.round((stats.sessions / totalSessions) * 100),
  })).sort((a, b) => b.sessions - a.sessions);

  const trafficSources: ChannelMetric[] = sourceAttributions.map((s) => ({
    name: s.source,
    count: s.sessions,
    percentage: s.share_percentage,
  }));

  const totalDevices = Array.from(deviceSessionsMap.values()).reduce((sum, set) => sum + set.size, 0) || 1;
  const deviceBreakdown: ChannelMetric[] = Array.from(deviceSessionsMap.entries()).map(([device, set]) => ({
    name: device.charAt(0).toUpperCase() + device.slice(1),
    count: set.size,
    percentage: Math.round((set.size / totalDevices) * 100),
  })).sort((a, b) => b.count - a.count);

  return { trafficSources, sourceAttributions, deviceBreakdown };
}

/**
 * Calculates peak operational periods differentiating browsing, ordering, and revenue.
 */
export function computePeakPeriods(rawEvents: any[], orders: any[]): PeakPeriodsMetric {
  const trafficHours = new Array(24).fill(0);
  const orderHours = new Array(24).fill(0);
  const revenueHours = new Array(24).fill(0);

  let totalEvents = 0;
  rawEvents.forEach((evt) => {
    if (evt.created_at) {
      const h = new Date(evt.created_at).getHours();
      trafficHours[h] += 1;
      totalEvents++;
    }
  });

  let totalOrders = 0;
  orders.forEach((o) => {
    if (o.created_at) {
      const h = new Date(o.created_at).getHours();
      orderHours[h] += 1;
      totalOrders++;
      if (String(o.status || '').toUpperCase() === 'COMPLETED') {
        revenueHours[h] += Number(o.total_amount) || 0;
      }
    }
  });

  const maxTraffic = Math.max(...trafficHours);
  const maxOrders = Math.max(...orderHours);
  const maxRevenue = Math.max(...revenueHours);

  const maxTrafficHour = maxTraffic > 0 ? trafficHours.indexOf(maxTraffic) : -1;
  const maxOrderHour = maxOrders > 0 ? orderHours.indexOf(maxOrders) : -1;
  const maxRevenueHour = maxRevenue > 0 ? revenueHours.indexOf(maxRevenue) : -1;

  const formatHour = (h: number) => `${String(h).padStart(2, '0')}:00–${String((h + 1) % 24).padStart(2, '0')}:00`;

  return {
    busiest_hours: maxOrderHour >= 0 ? formatHour(maxOrderHour) : 'No peak activity yet',
    peak_traffic_hour: maxTrafficHour >= 0 ? formatHour(maxTrafficHour) : 'No traffic recorded yet',
    peak_ordering_hour: maxOrderHour >= 0 ? formatHour(maxOrderHour) : 'No orders recorded yet',
    peak_revenue_hour: maxRevenueHour >= 0 ? formatHour(maxRevenueHour) : 'No revenue recorded yet',
    peak_traffic_count: maxTraffic,
    peak_orders_count: maxOrders,
    peak_revenue_amount: maxRevenue,
    total_events_evaluated: totalEvents,
    total_orders_evaluated: totalOrders,
  };
}

/**
 * Synthesizes automated executive findings from underlying aggregated metrics.
 */
export function generateAutomatedInsights(
  funnel: ConversionFunnelMetrics,
  itemConversions: { topRevenue: ItemPerformanceMetric[]; topConversion: ItemPerformanceMetric[] },
  unmetSearches: SearchInsightItem[],
  sources: SourceAttributionMetric[],
  peak: PeakPeriodsMetric,
  currency = 'MAD'
): AutomatedInsightFinding[] {
  const findings: AutomatedInsightFinding[] = [];

  // Traffic & Funnel Finding
  findings.push({
    id: 'f-1',
    type: 'traffic',
    text: `Menu recorded ${funnel.sessions.toLocaleString()} sessions with an overall conversion rate of ${funnel.overall_conversion_rate}%.`,
  });

  // Top Revenue Finding
  if (itemConversions.topRevenue.length > 0) {
    const top = itemConversions.topRevenue[0];
    findings.push({
      id: 'f-2',
      type: 'conversion',
      text: `${top.name} generated the highest revenue (${(top.count || 0).toLocaleString()} ${currency}).`,
    });
  }

  // Strong Conversion Finding
  if (itemConversions.topConversion.length > 0) {
    const top = itemConversions.topConversion[0];
    findings.push({
      id: 'f-3',
      type: 'conversion',
      text: `${top.name} demonstrated exceptional demand with a ${top.count}% order conversion rate.`,
    });
  }

  // Unmet Demand Finding
  if (unmetSearches.length > 0) {
    const search = unmetSearches[0];
    findings.push({
      id: 'f-4',
      type: 'alert',
      text: `Guests searched for "${search.query}" ${search.count} times, but no matching menu item was found.`,
    });
  }

  // Traffic Attribution Finding
  if (sources.length > 0) {
    const topSrc = sources[0];
    findings.push({
      id: 'f-5',
      type: 'attribution',
      text: `${topSrc.source} generated ${topSrc.share_percentage}% of guest traffic and ${topSrc.orders} total orders.`,
    });
  }

  return findings;
}
